import React, { useState } from "react";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { Check, Copy, Mail, MessageSquare, X } from "lucide-react";

import { createAxiosInstance } from "api/axiosConfig";
import { showMutationError } from "utils/showMutationError";

import styles from "./VideoShareActions.module.css";

const VideoShareActions = ({
  patient,
  appointment,
  consultation,
}) => {
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isSendingSms, setIsSendingSms] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");

  const tenantVal =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");

  const appointmentId = appointment?.id || consultation?.appointment?.id;

  // The link the backend stored when it set up the meeting — the one Send Email and Send SMS
  // send. Building it here left out the `v` that picks the patient's join form (KIN-1953), so
  // a copied link could open the wrong form for the practice.
  const participantUrl = appointment?.participantVideoUrl || null;

  const handleCopyUrl = async () => {
    if (!participantUrl) {
      toast.error("Video meeting has not been set up for this appointment");
      return;
    }
    try {
      await navigator.clipboard.writeText(participantUrl);
      setCopied(true);
      toast.success("Video link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy link");
    }
  };

  const handleOpenEmailModal = () => {
    setEmailAddress(patient?.email || "");
    setShowEmailModal(true);
  };

  const handleOpenSmsModal = () => {
    setMobileNumber(patient?.mobilePhone || "");
    setShowSmsModal(true);
  };

  const handleSendEmail = async () => {
    if (!emailAddress.trim()) {
      toast.error("Please enter an email address");
      return;
    }

    if (!appointmentId) {
      toast.error("Appointment ID not found");
      return;
    }

    setIsSendingEmail(true);

    try {
      const axiosInstance = await createAxiosInstance();
      await axiosInstance.post(
        `${tenantVal}/video/appointments/${appointmentId}/send-invite`,
        { patientEmail: emailAddress }
      );

      toast.success("Email invitation sent successfully");
      setShowEmailModal(false);
    } catch (error) {
      showMutationError(error, "Failed to send email invitation");
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleSendSms = async () => {
    if (!mobileNumber.trim()) {
      toast.error("Please enter a mobile number");
      return;
    }

    if (!appointmentId) {
      toast.error("Appointment ID not found");
      return;
    }

    setIsSendingSms(true);

    try {
      const axiosInstance = await createAxiosInstance();
      await axiosInstance.post(
        `${tenantVal}/video/appointments/${appointmentId}/send-sms`,
        { mobilePhone: mobileNumber }
      );

      toast.success("SMS sent successfully");
      setShowSmsModal(false);
    } catch (error) {
      showMutationError(error, "Failed to send SMS");
    } finally {
      setIsSendingSms(false);
    }
  };

  return (
    <>
      <div className={styles.container}>
        <p className={styles.title}>Share video link with patient</p>
        <div className={styles.buttonGroup}>
          <button
            className={styles.actionButton}
            onClick={handleOpenEmailModal}
            title="Send video link via email"
          >
            <Mail size={18} />
            <span>Send Email</span>
          </button>

          <button
            className={styles.actionButton}
            onClick={handleOpenSmsModal}
            title="Send video link via SMS"
          >
            <MessageSquare size={18} />
            <span>Send SMS</span>
          </button>

          <button
            className={`${styles.actionButton} ${styles.copyButton}`}
            onClick={handleCopyUrl}
            disabled={!participantUrl}
            title={participantUrl ? "Copy video link to clipboard" : "Video link not available yet"}
          >
            {copied ? <Check size={18} /> : <Copy size={18} />}
            <span>{copied ? "Copied!" : "Copy Link"}</span>
          </button>
        </div>
      </div>

      {showEmailModal && (
        <div className={styles.modalOverlay} onClick={() => setShowEmailModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Send Video Link via Email</h3>
              <button
                className={styles.closeButton}
                onClick={() => setShowEmailModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <p className={styles.modalDescription}>
                Send the video consultation link to the patient's email address.
              </p>
              <div className={styles.inputGroup}>
                <label htmlFor="emailAddress">Email Address</label>
                <input
                  id="emailAddress"
                  type="email"
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  placeholder="Enter email address"
                  className={styles.input}
                />
              </div>
              {patient?.firstName && (
                <p className={styles.patientInfo}>
                  Patient: {patient.firstName} {patient.surname}
                </p>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button
                className={styles.cancelButton}
                onClick={() => setShowEmailModal(false)}
                disabled={isSendingEmail}
              >
                Cancel
              </button>
              <button
                className={styles.sendButton}
                onClick={handleSendEmail}
                disabled={isSendingEmail || !emailAddress.trim()}
              >
                {isSendingEmail ? "Sending..." : "Send Email"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSmsModal && (
        <div className={styles.modalOverlay} onClick={() => setShowSmsModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Send Video Link via SMS</h3>
              <button
                className={styles.closeButton}
                onClick={() => setShowSmsModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <p className={styles.modalDescription}>
                Send the video consultation link to the patient's mobile number.
              </p>
              <div className={styles.inputGroup}>
                <label htmlFor="mobileNumber">Mobile Number</label>
                <input
                  id="mobileNumber"
                  type="tel"
                  value={mobileNumber}
                  onChange={(e) => setMobileNumber(e.target.value)}
                  placeholder="Enter mobile number"
                  className={styles.input}
                />
              </div>
              {patient?.firstName && (
                <p className={styles.patientInfo}>
                  Patient: {patient.firstName} {patient.surname}
                </p>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button
                className={styles.cancelButton}
                onClick={() => setShowSmsModal(false)}
                disabled={isSendingSms}
              >
                Cancel
              </button>
              <button
                className={styles.sendButton}
                onClick={handleSendSms}
                disabled={isSendingSms || !mobileNumber.trim()}
              >
                {isSendingSms ? "Sending..." : "Send SMS"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export { VideoShareActions };